import type { ChannelId, InboxItem, Participant } from '../fixtures/domain.js';
import { Avatar } from '../components/Avatar.js';
import { Checkbox } from '../components/Checkbox.js';
import { Icon } from '../components/Icon.js';
import { PlainMessageText } from '../components/MessageMarkdown.js';
import { formatInboxTime, plural } from '../util/format.js';
import { AssistantMark } from '../components/AssistantMark.js';
import { flaggedChats, unreadChats } from './stack.js';
import styles from './StackedPreview.module.css';

interface StackedPreviewProps {
  counterpart: Participant;
  /** This person's applications, newest first. Always two or more. */
  items: InboxItem[];
  /** The conversation open in the middle panel, whether or not it is in here. */
  selectedId: ChannelId | null;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelect: (item: InboxItem) => void;
  /**
   * Selection is the inbox's, exactly as on a plain row — and opt-in for the
   * same reason `ChannelPreview` makes it opt-in. The widget's list is the reply
   * loop, so it draws this row without a checkbox and leaves the three props
   * below alone.
   */
  selectable?: boolean;
  selectedIds?: Set<ChannelId>;
  selecting?: boolean;
  /** Tick or clear a set of chats at once — what the row's own box does. */
  onSelectMany?: (channelIds: ChannelId[], selected: boolean) => void;
}

/** Frozen so an unselectable row's default cannot churn its memo inputs. */
const NO_SELECTION: ReadonlySet<ChannelId> = new Set();

/**
 * One person's several conversations, as a single row that opens.
 *
 * The shape of the answer to "an employer has three postings and one jobseeker
 * applied to all three". Collapsed, the row is the person and the second line
 * is a summary of what it holds — with three chats there is no *the* preview,
 * and the question the row answers first is no longer "what did they say" but
 * "how much of this is mine to do". So: how many chats, and how many of them
 * are unread. A count both halves of, rather than a dot: the dot would say
 * something is new without saying how much, on the one row in the column that
 * stands for several.
 *
 * No timestamp up here. A time belongs to a conversation; the column is already
 * ordered by the newest of this person's, and each thread inside says its own.
 *
 * ## What a click does
 *
 * The row is not a conversation, so it cannot open one, and it deliberately
 * does not pick a conversation on the employer's behalf: opening the newest of
 * three would mark it read and answer a question the employer had not asked.
 * Pressing the row expands it instead, and every chat becomes a proper row —
 * led by a disc (a briefcase for an application, the bubble for a direct chat),
 * titled by the posting, with its own last message, its own time and its own
 * unread mark.
 *
 * A popover was the other option and is worse here. This column is scrolled and
 * scanned; a floating menu covers the rows around the one it belongs to, closes
 * on scroll, and once it has closed nothing on screen says which conversation
 * the panel on the right belongs to. Opening in place costs the rows below a
 * downward shift and keeps everything else.
 *
 * ## Saying which one is open, while shut
 *
 * A row does not spring open around the conversation on screen — expanding is
 * the employer's and nothing derives it. So a collapsed row carries that fact
 * itself: it takes the same active fill a plain row would, and points at the
 * open chat without claiming to be it. Open, the thread's own row takes the
 * fill instead, because two filled rows in one block would be the row claiming
 * the highlight belongs to the person rather than to one conversation.
 */
export function StackedPreview({
  counterpart,
  items,
  selectedId,
  expanded,
  onToggleExpand,
  onSelect,
  selectable,
  selectedIds = NO_SELECTION as Set<ChannelId>,
  selecting = false,
  onSelectMany,
}: StackedPreviewProps) {
  const unread = unreadChats(items);
  const flagged = flaggedChats(items);

  const ids = items.map((item) => item.channel.id);
  const picked = ids.filter((id) => selectedIds.has(id));
  /**
   * The row's box speaks for everything under it: ticked when the whole row is,
   * a dash while part of it is. Standard tree semantics, and the honest ones —
   * the row visually contains those chats, so a box on it that meant anything
   * narrower would be a box that lies about its own row.
   *
   * A dash clears to a full tick rather than to nothing, matching the list's own
   * select-all: a partial state means "not finished picking", so the press
   * finishes it.
   */
  const allPicked = picked.length === ids.length;
  const holdsOpenChat = selectedId !== null && ids.includes(selectedId);

  return (
    <div
      className={styles.stack}
      data-open={expanded || undefined}
      data-selecting={selecting || undefined}
    >
      <div
        /* Collapsed, the row stands in for the chat on screen and takes its
           fill. Open, the sub-row below is that chat and takes it instead —
           two filled rows in one block would be the row claiming the highlight
           belongs to the person rather than to one application. */
        className={
          holdsOpenChat && !expanded ? `${styles.headWrap} ${styles.isActive}` : styles.headWrap
        }
        data-selecting={selecting || undefined}
      >
        {/* Both halves are the selectable row's, so both are withheld together.
            The spacer is the nudge that makes room for the box: on a touch
            device it is a permanent 28px, and left in on a list that has no
            checkbox at all (the widget's) it would indent this row's face 28px
            past every plain row above it. */}
        {selectable && (
          <>
            <label className={styles.zone}>
              <Checkbox
                checked={allPicked}
                indeterminate={picked.length > 0 && !allPicked}
                label={`Select all ${plural(items.length, 'chat')} with ${counterpart.name}`}
                onChange={() => onSelectMany?.(ids, !allPicked)}
              />
            </label>
            <span className={styles.spacer} aria-hidden="true" />
          </>
        )}

        <div className={styles.head}>
          {/* Laid under the row rather than wrapped around it: the checkbox
              zone beside it is a control of its own, and a button inside a
              button is invalid markup that browsers resolve by dropping one of
              them. So the expander is a full-bleed cover at z-index 0. */}
          <button
            type="button"
            className={styles.cover}
            aria-expanded={expanded}
            aria-label={`${counterpart.name}, ${plural(items.length, 'chat')}`}
            onClick={onToggleExpand}
          />
          <Avatar name={counterpart.name} src={counterpart.avatarUrl} locked={counterpart.locked} />
          <span className={styles.body}>
            <span className={styles.line}>
              <span className={styles.nameWrap}>
                {counterpart.online !== undefined && (
                  <span className={counterpart.online ? styles.dotOnline : styles.dotOffline} />
                )}
                <span data-ph-mask className={styles.name}>
                  {counterpart.name}
                </span>
              </span>
            </span>

            {/* What the row holds, under the name rather than beside it. Beside,
                the two fought for one line's width and the name lost, clipping
                at "Aisha Ra…" to keep a count whole. No time up here either: a
                timestamp belongs to a conversation, the column is already
                ordered by the newest of them, and each thread says its own. */}
            <span className={styles.summary}>
              <Icon name="chatBubble" size={13} />
              {plural(items.length, 'chat')}
              {/* Visible text rather than a dot, so the unread state is also the
                  announced one and needs no hidden span beside it. */}
              {unread > 0 && <span className={styles.summaryUnread}>&middot; {unread} unread</span>}
              {/* A mark rather than a count, unlike the unread beside it. The
                  count answers "how much of this is mine to do", which is a
                  question about volume; this answers "is a decision waiting in
                  here", which is answered the same way by one thread or three.
                  Without it a folded row is the one place in the inbox a
                  decision can hide completely — the header says nothing and
                  expanding is the only way to find out. */}
              {flagged > 0 && <AssistantMark />}
            </span>
          </span>
          <span className={styles.chevron} aria-hidden="true">
            <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={16} />
          </span>
        </div>
      </div>

      {expanded && (
        <div className={styles.subs}>
          {items.map((item) => (
            <StackChatRow
              key={item.channel.id}
              item={item}
              active={item.channel.id === selectedId}
              onClick={() => onSelect(item)}
              selectable={selectable}
              selected={selectedIds.has(item.channel.id)}
              selecting={selecting}
              onToggleSelect={() =>
                onSelectMany?.([item.channel.id], !selectedIds.has(item.channel.id))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One application inside an open row.
 *
 * Not a `ChannelPreview`: the name and the face are the heading above it, and
 * repeating them on every child is the duplication this whole row exists to
 * remove. What is left is what actually differs between them — which posting,
 * what was last said about it, when, and whether it holds something unread.
 *
 * A disc leads it where the face would be on a plain row, so the two land their
 * text on the same column: the briefcase for an application, the chat bubble for
 * the one conversation with this person that is about no posting at all.
 */
function StackChatRow({
  item,
  active,
  onClick,
  selectable,
  selected,
  selecting,
  onToggleSelect,
}: {
  item: InboxItem;
  active: boolean;
  onClick: () => void;
  selectable?: boolean;
  selected: boolean;
  selecting: boolean;
  onToggleSelect: () => void;
}) {
  const { channel } = item;
  const job = channel.jobMeta;
  // A direct chat is titled by what it is: there is no posting to name, and an
  // untitled row beside two titled ones reads as one that failed to load.
  const title = job?.jobTitle ?? 'Direct message';
  const hasUnread = channel.unreadCount > 0;

  return (
    <div
      className={active ? `${styles.sub} ${styles.subActive}` : styles.sub}
      data-selecting={selecting || undefined}
    >
      {selectable && (
        <label className={styles.subZone}>
          <Checkbox
            checked={selected}
            label={`Select the ${title} chat with ${item.counterpart.name}`}
            onChange={onToggleSelect}
          />
        </label>
      )}
      <button type="button" className={styles.subRow} onClick={onClick}>
        <span className={styles.subDisc} aria-hidden="true">
          <Icon name={job ? 'briefcase' : 'chatBubble'} size={14} />
        </span>
        <span className={styles.subBody}>
          <span className={styles.subLine}>
            <span className={styles.subJobText}>{title}</span>
            {/* No match badge down here, unlike the plain row above. These rows
                exist to tell four applications by the same person apart, and the
                thing that does that is the title, what was last said and when —
                a label most of them carry equally answers a question nobody is
                asking at this depth. The plain rows keep theirs. */}
            <span className={styles.subTime}>{formatInboxTime(channel.lastActivityAt)}</span>
          </span>
          <span className={styles.subLine}>
            <span className={hasUnread ? styles.subPreviewUnread : styles.subPreview}>
              {/* The same three cases the plain row draws, for the same reasons:
                  an attachment-only message has no text and would render the row
                  blank (AI-1738), and a channel nobody has spoken in yet says so
                  rather than showing an empty line (AI-2664). */}
              {channel.lastMessageHasAttachment ? (
                <span className={styles.subAttachment}>
                  <Icon name={channel.lastMessageIsVoice ? 'audioLines' : 'paperclip'} size={13} />
                  <span>
                    {channel.lastMessageText !== '' ? (
                      <PlainMessageText text={channel.lastMessageText} />
                    ) : channel.lastMessageIsVoice ? (
                      'Voice note'
                    ) : (
                      'Attachment'
                    )}
                  </span>
                </span>
              ) : channel.lastMessageText === '' ? (
                <span className={styles.subEmpty}>No messages yet</span>
              ) : (
                <PlainMessageText text={channel.lastMessageText} />
              )}
            </span>
            {/* Weight is the mark on these rows too — see `ChannelPreview` for
                why red is not. The word is for the screen readers bold does not
                reach. */}
            {channel.unreadCount > 0 && <span className={styles.srOnly}>Unread</span>}
            {/* The same mark the plain row wears, for the same reason: this is
                the row the employer would actually open. */}
            {channel.assistantFlag === true && <AssistantMark />}
          </span>
        </span>
      </button>
    </div>
  );
}
